import { useState, useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import type { Post, UserProfile, ClubRole } from "@/api/clubsApi";
import { useVotePollMutation, useDeletePostMutation, useTogglePostLikeMutation, useTogglePostPinMutation } from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";
import { WebMediaViewer } from "@/components/ui/WebMediaViewer";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/Dialog";
import { ReportContentDialog } from "@/components/ui/ReportContentDialog";
import { Button } from "@/components/ui/Button";
import { cn } from "@/components/ui/cn";
import { userDisplayName } from "@tinyclub/shared/utils";
import { LocationView } from "@/components/location";
import { PollDisplay } from "./PollDisplay";
import { RichBodyText, type MentionMap } from "@/components/ui/RichBodyText";
import { LinkPreview } from "@/components/ui/LinkPreview";
import { GifPreview } from "@/components/ui/GifPreview";
import { PostLikesDialog } from "./PostLikesDialog";
import { extractFirstUrl, countUrls, isGifUrl } from "@/utils/linkUtils";

interface PostCardProps {
  post: Post;
  author?: UserProfile | null;
  clubId: string;
  currentUserId?: string | null;
  userRole?: ClubRole | null;
  className?: string;
  onClick?: () => void;
  onCommentClick?: () => void;
  onEdit?: (post: Post) => void;
  onReport?: (post: Post) => void;
  mentionMap?: MentionMap;
}

export function PostCard({
  post,
  author,
  clubId,
  currentUserId,
  userRole,
  className,
  onClick,
  onCommentClick,
  onEdit,
  onReport,
  mentionMap,
}: PostCardProps) {
  const [viewerMedia, setViewerMedia] = useState<{
    src: string;
    type: "image" | "video";
  } | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showLikesDialog, setShowLikesDialog] = useState(false);
  const [votePoll, { isLoading: isVoting }] = useVotePollMutation();
  const [deletePost, { isLoading: isDeleting }] = useDeletePostMutation();
  const [toggleLike] = useTogglePostLikeMutation();
  const [togglePin] = useTogglePostPinMutation();

  // Permission checks
  const isPostAuthor = currentUserId && post.author_id === currentUserId;
  const isAdminOrManager = userRole === "owner" || userRole === "cohost";
  const canDelete = isPostAuthor || isAdminOrManager;
  const canEdit = isPostAuthor;
  const showOverflowMenu = currentUserId; // Show menu if user is logged in

  // Handle delete
  const handleDelete = async () => {
    try {
      await deletePost({ postId: post.id, clubId }).unwrap();
      setShowDeleteDialog(false);
    } catch (error) {
      console.error("Failed to delete post:", error);
    }
  };

  // Handle pin toggle
  const handlePinToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await togglePin({ postId: post.id, clubId });
    } catch (error) {
      console.error("Failed to toggle pin:", error);
    }
  };

  // Handle like toggle
  const handleLikeToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUserId) return;

    try {
      await toggleLike({
        postId: post.id,
        clubId,
        userId: currentUserId,
      });
    } catch (error) {
      console.error("Failed to toggle like:", error);
    }
  };

  // Handle poll vote
  const handlePollVote = async (optionId: string) => {
    if (!post.poll || !currentUserId) return;

    try {
      await votePoll({
        pollId: post.poll.id,
        optionId,
        postId: post.id,
        clubId,
        previousOptionId: post.poll.user_vote_option_id,
        userId: currentUserId,
      });
    } catch (error) {
      console.error("Failed to vote on poll:", error);
    }
  };

  // Format the timestamp
  const formattedTime = formatRelativeTime(post.created_at);

  // Get author display info
  const authorName = author ? userDisplayName(author) : "Unknown";
  const authorInitials = getInitials(authorName);
  const authorAvatar = author?.avatar_small || author?.avatar_medium;

  // Get first media item if exists
  const firstMedia = post.post_media?.[0];
  const hasImage = firstMedia?.kind === "image";
  const hasVideo = firstMedia?.kind === "video";
  const imageUrl =
    firstMedia?.image_medium || firstMedia?.image_full || firstMedia?.image_small;
  const fullImageUrl = firstMedia?.image_full || firstMedia?.image_medium;
  const videoUrl = firstMedia?.video_url;

  // Video processing state (columns exist on posts table but not in TS type)
  const isProcessing = (post as any).is_processing === true;
  const processingStatus = (post as any).processing_status as string | undefined;
  const isProcessingVideo = processingStatus === "processing";
  const isFailedVideo = processingStatus === "failed";
  const hasProcessingMedia = hasVideo && (isProcessingVideo || isFailedVideo || (!videoUrl && !imageUrl));

  // Determine if we should show link preview or GIF preview
  const { firstUrl, showGifPreview, showLinkPreview } = useMemo(() => {
    const bodyText = post.body_text || "";
    const firstUrl = extractFirstUrl(bodyText);
    const urlCount = countUrls(bodyText);
    const isGif = isGifUrl(firstUrl);

    // Show GIF preview if there's exactly 1 URL and it's a GIF
    const showGifPreview = urlCount === 1 && isGif && firstUrl !== null;

    // Show link preview if there's exactly 1 URL and it's NOT a GIF and no media attached
    const showLinkPreview = urlCount === 1 && !isGif && firstUrl !== null && !hasImage && !hasVideo;

    return { firstUrl, showGifPreview, showLinkPreview };
  }, [post.body_text, hasImage, hasVideo]);

  // Handle media click - use capture phase and prevent default to ensure we handle first
  const handleMediaClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Set media viewer state
    if (hasVideo && videoUrl) {
      setViewerMedia({ src: videoUrl, type: "video" });
    } else if (hasImage && fullImageUrl) {
      setViewerMedia({ src: fullImageUrl, type: "image" });
    }
  };

  // Card click handler (navigates to post detail)
  const handleCardClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        className={cn(
          "px-5 py-4", // 20px horizontal, 16px vertical padding like mobile
          onClick && "cursor-pointer",
          className
        )}
      >
        {/* Pinned indicator */}
        {post.pinned && (
          <div className="flex items-center gap-1.5 text-body-3 text-text-tertiary mb-3">
            <Icon name="thumbtack-outline" size={12} />
            <span>Pinned</span>
          </div>
        )}

        {/* Two-column layout matching Figma: gap-16px between avatar and content */}
        <div className="flex gap-4 items-start">
          {/* Avatar - explicit 32x32px */}
          <div
            className="rounded-full overflow-hidden bg-surface-2 flex-shrink-0"
            style={{ width: 32, height: 32 }}
          >
            {authorAvatar ? (
              <img
                src={authorAvatar}
                alt={authorName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs font-medium text-text-secondary">
                {authorInitials}
              </div>
            )}
          </div>

          {/* Right column - All content */}
          <div className="flex-1 min-w-0">
            {/* Author info, timestamp, and overflow menu - 32px height to match avatar */}
            <div className="flex items-center gap-1.5 h-8 mb-1">
              {/* Name and date grouped together, baseline-aligned */}
              <div className="flex items-baseline gap-1.5 min-w-0 flex-1">
                <span className="text-body-2-medium text-text-primary truncate">
                  {authorName}
                </span>
                <span className="text-body-3 text-text-tertiary flex-shrink-0">
                  {formattedTime}
                </span>
              </div>
              {/* Processing spinner (replaces overflow menu while processing) */}
              {isProcessing && (
                <div className="p-1 -mr-1">
                  <div className="w-4 h-4 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {/* Overflow menu */}
              {showOverflowMenu && !isProcessing && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="p-1 -mr-1 text-text-tertiary hover:text-text-secondary rounded-lg hover:bg-surface-2 transition-colors"
                      aria-label="Post options"
                    >
                      <Icon name="ellipsis" size={16} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canEdit && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit?.(post);
                        }}
                      >
                        <Icon name="pen" size={14} />
                        Edit
                      </DropdownMenuItem>
                    )}
                    {isAdminOrManager && (
                      <DropdownMenuItem onClick={handlePinToggle}>
                        <Icon name={post.pinned ? "thumbtack-slash" : "thumbtack-outline"} size={14} />
                        {post.pinned ? "Unpin" : "Pin"}
                      </DropdownMenuItem>
                    )}
                    {post.body_text && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(post.body_text);
                        }}
                      >
                        <Icon name="copy" size={14} />
                        Copy text
                      </DropdownMenuItem>
                    )}
                    {!isPostAuthor && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowReportDialog(true);
                        }}
                      >
                        <Icon name="flag" size={14} />
                        Report
                      </DropdownMenuItem>
                    )}
                    {canDelete && (
                      <DropdownMenuItem
                        destructive
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteDialog(true);
                        }}
                      >
                        <Icon name="trash" size={14} />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Post body with rich text */}
            {post.body_text && (
              <RichBodyText
                text={post.body_text}
                mentionMap={mentionMap}
                className="mb-3"
              />
            )}

            {/* GIF preview */}
            {showGifPreview && firstUrl && (
              <div className="mb-3">
                <GifPreview url={firstUrl} />
              </div>
            )}

            {/* Link preview */}
            {showLinkPreview && firstUrl && (
              <div className="mb-3">
                <LinkPreview url={firstUrl} />
              </div>
            )}

            {/* Poll display */}
            {post.poll && (
              <div className="mb-3">
                <PollDisplay
                  poll={post.poll}
                  onVote={handlePollVote}
                  isVoting={isVoting}
                  currentUserId={currentUserId}
                />
              </div>
            )}

            {/* Location display */}
            {post.location_object && (
              <div className="mb-3">
                <LocationView
                  location={post.location_object}
                  locationName={post.location_name}
                />
              </div>
            )}

            {/* Video processing placeholder */}
            {hasProcessingMedia && (
              <div
                className="mb-3 rounded-xl overflow-hidden bg-surface-2 relative"
                style={{
                  aspectRatio: firstMedia?.width && firstMedia?.height
                    ? `${firstMedia.width} / ${firstMedia.height}`
                    : "16 / 9",
                  boxShadow: "inset 0 0 0 1px var(--color-border-tertiary)",
                }}
              >
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt=""
                    className="w-full h-full object-cover absolute inset-0"
                  />
                )}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center rounded-2xl bg-white/60 dark:bg-black/40 backdrop-blur-xl p-6">
                    {isFailedVideo ? (
                      <>
                        <Icon name="exclamation" size={24} className="text-text-primary mb-2" />
                        <span className="text-body-2-medium text-text-primary">
                          Processing failed
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="w-6 h-6 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin mb-2" />
                        <span className="text-body-2-medium text-text-secondary">
                          Processing video...
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Media preview - clickable, uses actual aspect ratio */}
            {(hasImage || hasVideo) && imageUrl && !hasProcessingMedia && (
              <div
                className="mb-3 rounded-xl overflow-hidden bg-surface-2 relative cursor-pointer"
                onClickCapture={handleMediaClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleMediaClick(e as unknown as React.MouseEvent);
                  }
                }}
                style={{
                  // Inner border like mobile
                  boxShadow: "inset 0 0 0 1px var(--color-border-tertiary)",
                }}
              >
                <img
                  src={imageUrl}
                  alt=""
                  className="w-full block"
                  style={{
                    aspectRatio:
                      firstMedia?.width && firstMedia?.height
                        ? `${firstMedia.width} / ${firstMedia.height}`
                        : undefined,
                  }}
                />
                {hasVideo && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                      <Icon name="play" size={20} className="text-text-primary ml-0.5" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Engagement stats (matches mobile action row) */}
            <div className="flex items-center gap-4 text-body-3 text-text-tertiary">
              {/* Like button */}
              <button
                onClick={handleLikeToggle}
                disabled={!currentUserId}
                className={cn(
                  "flex items-center gap-1.5 transition-colors",
                  currentUserId && "hover:text-red"
                )}
              >
                <Icon
                  name={post.liked ? "heart" : "heart-outline"}
                  size={20}
                  className={post.liked ? "text-red" : ""}
                />
              </button>
              {/* Clickable like count — opens who liked dialog */}
              {(post.like_count || 0) > 0 ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLikesDialog(true);
                  }}
                  className="hover:underline transition-colors -ml-2"
                >
                  {post.like_count}
                </button>
              ) : (
                <span className="-ml-2">0</span>
              )}

              {/* Comment button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onCommentClick?.();
                }}
                className="flex items-center gap-1.5 hover:text-text-secondary transition-colors"
              >
                <Icon name="comment-outline" size={20} />
                <span>{post.comment_count || 0}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Media viewer modal */}
      {viewerMedia && (
        <WebMediaViewer
          src={viewerMedia.src}
          type={viewerMedia.type}
          onClose={() => setViewerMedia(null)}
        />
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Delete Post</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this post? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-3 pt-4">
            <Button
              variant="outline"
              className="w-full h-auto py-2 rounded-full"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="w-full h-auto py-2 rounded-full"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report content dialog */}
      <ReportContentDialog
        open={showReportDialog}
        onOpenChange={setShowReportDialog}
        itemId={post.id}
        itemType="post"
      />

      {/* Post likes dialog */}
      <PostLikesDialog
        postId={post.id}
        open={showLikesDialog}
        onOpenChange={setShowLikesDialog}
      />
    </>
  );
}

// Helper function to format relative time
function formatRelativeTime(dateString: string): string {
  const date = DateTime.fromISO(dateString);
  const now = DateTime.now();
  const diff = now.diff(date, ["days", "hours", "minutes"]);

  if (diff.days >= 7) {
    return date.toFormat("MMM d");
  } else if (diff.days >= 1) {
    return `${Math.floor(diff.days)}d`;
  } else if (diff.hours >= 1) {
    return `${Math.floor(diff.hours)}h`;
  } else if (diff.minutes >= 1) {
    return `${Math.floor(diff.minutes)}m`;
  } else {
    return "now";
  }
}

// Helper function to get initials from name
function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}
